/**
 * Schema Builder Context
 *
 * Provides state management for the schema builder UI using the
 * Context + Reducer pattern.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";
import type {
  SchemaBuilderTable,
  SchemaBuilderColumn,
  SchemaBuilderRelationship,
  SchemaBuilderState,
  SimpleColumnType,
  OnDeleteBehavior,
} from "../../types";

// ============================================================================
// Actions
// ============================================================================

type SchemaBuilderAction =
  | { type: "LOAD_TABLES"; payload: SchemaBuilderTable[] }
  | { type: "LOAD_RELATIONSHIPS"; payload: SchemaBuilderRelationship[] }
  | { type: "ADD_TABLE"; payload: SchemaBuilderTable }
  | {
      type: "UPDATE_TABLE";
      payload: { tableId: string; updates: Partial<SchemaBuilderTable> };
    }
  | { type: "DELETE_TABLE"; payload: string }
  | { type: "SELECT_TABLE"; payload: string | null }
  | {
      type: "ADD_COLUMN";
      payload: { tableId: string; column: SchemaBuilderColumn };
    }
  | {
      type: "UPDATE_COLUMN";
      payload: {
        tableId: string;
        columnId: string;
        updates: Partial<SchemaBuilderColumn>;
      };
    }
  | { type: "DELETE_COLUMN"; payload: { tableId: string; columnId: string } }
  | {
      type: "REORDER_COLUMNS";
      payload: { tableId: string; columns: SchemaBuilderColumn[] };
    }
  | { type: "ADD_RELATIONSHIP"; payload: SchemaBuilderRelationship }
  | { type: "DELETE_RELATIONSHIP"; payload: string }
  | { type: "SET_DIRTY"; payload: boolean }
  | { type: "SET_SAVING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "RESET" };

// ============================================================================
// Reducer
// ============================================================================

const initialState: SchemaBuilderState = {
  tables: [],
  selectedTableId: null,
  relationships: [],
  pendingTableDeletions: [],
  isDirty: false,
  isSaving: false,
  error: null,
};

function reducer(
  state: SchemaBuilderState,
  action: SchemaBuilderAction,
): SchemaBuilderState {
  switch (action.type) {
    case "LOAD_TABLES":
      return {
        ...state,
        tables: action.payload,
        selectedTableId:
          action.payload.length > 0 ? action.payload[0].tempId : null,
        pendingTableDeletions: [],
        isDirty: false,
      };

    case "LOAD_RELATIONSHIPS":
      return {
        ...state,
        relationships: action.payload,
      };

    case "ADD_TABLE":
      return {
        ...state,
        tables: [...state.tables, action.payload],
        selectedTableId: action.payload.tempId,
        isDirty: true,
      };

    case "UPDATE_TABLE":
      return {
        ...state,
        tables: state.tables.map((t) =>
          t.tempId === action.payload.tableId
            ? { ...t, ...action.payload.updates }
            : t,
        ),
        isDirty: true,
      };

    case "DELETE_TABLE": {
      const deletedTable = state.tables.find(
        (t) => t.tempId === action.payload,
      );
      const newTables = state.tables.filter((t) => t.tempId !== action.payload);
      return {
        ...state,
        tables: newTables,
        selectedTableId:
          state.selectedTableId === action.payload
            ? newTables.length > 0
              ? newTables[0].tempId
              : null
            : state.selectedTableId,
        relationships: state.relationships.filter(
          (r) =>
            r.sourceTableId !== action.payload &&
            r.targetTableId !== action.payload,
        ),
        pendingTableDeletions: deletedTable?.id
          ? [...state.pendingTableDeletions, deletedTable.id]
          : state.pendingTableDeletions,
        isDirty: true,
      };
    }

    case "SELECT_TABLE":
      return {
        ...state,
        selectedTableId: action.payload,
      };

    case "ADD_COLUMN":
      return {
        ...state,
        tables: state.tables.map((t) =>
          t.tempId === action.payload.tableId
            ? { ...t, columns: [...t.columns, action.payload.column] }
            : t,
        ),
        isDirty: true,
      };

    case "UPDATE_COLUMN":
      return {
        ...state,
        tables: state.tables.map((t) =>
          t.tempId === action.payload.tableId
            ? {
                ...t,
                columns: t.columns.map((c) =>
                  c.tempId === action.payload.columnId
                    ? { ...c, ...action.payload.updates }
                    : c,
                ),
              }
            : t,
        ),
        isDirty: true,
      };

    case "DELETE_COLUMN":
      return {
        ...state,
        tables: state.tables.map((t) =>
          t.tempId === action.payload.tableId
            ? {
                ...t,
                columns: t.columns.filter(
                  (c) => c.tempId !== action.payload.columnId,
                ),
              }
            : t,
        ),
        isDirty: true,
      };

    case "REORDER_COLUMNS":
      return {
        ...state,
        tables: state.tables.map((t) =>
          t.tempId === action.payload.tableId
            ? { ...t, columns: action.payload.columns }
            : t,
        ),
        isDirty: true,
      };

    case "ADD_RELATIONSHIP":
      return {
        ...state,
        relationships: [...state.relationships, action.payload],
        isDirty: true,
      };

    case "DELETE_RELATIONSHIP":
      return {
        ...state,
        relationships: state.relationships.filter(
          (r) => r.tempId !== action.payload,
        ),
        isDirty: true,
      };

    case "SET_DIRTY":
      return { ...state, isDirty: action.payload };

    case "SET_SAVING":
      return { ...state, isSaving: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface SchemaBuilderContextValue {
  state: SchemaBuilderState;
  // Table actions
  addTable: () => void;
  updateTable: (tableId: string, updates: Partial<SchemaBuilderTable>) => void;
  deleteTable: (tableId: string) => void;
  selectTable: (tableId: string | null) => void;
  // Column actions
  addColumn: (tableId: string) => void;
  updateColumn: (
    tableId: string,
    columnId: string,
    updates: Partial<SchemaBuilderColumn>,
  ) => void;
  deleteColumn: (tableId: string, columnId: string) => void;
  reorderColumns: (tableId: string, columns: SchemaBuilderColumn[]) => void;
  // Relationship actions
  addRelationship: (
    sourceTableId: string,
    sourceColumnId: string,
    targetTableId: string,
    targetColumnId: string,
    onDelete?: OnDeleteBehavior,
  ) => void;
  deleteRelationship: (relationshipId: string) => void;
  // State actions
  loadTables: (tables: SchemaBuilderTable[]) => void;
  loadRelationships: (relationships: SchemaBuilderRelationship[]) => void;
  setDirty: (isDirty: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  // Helpers
  getSelectedTable: () => SchemaBuilderTable | undefined;
  getTableById: (tableId: string) => SchemaBuilderTable | undefined;
}

const SchemaBuilderContext = createContext<SchemaBuilderContextValue | null>(
  null,
);

// ============================================================================
// Provider
// ============================================================================

interface SchemaBuilderProviderProps {
  children: ReactNode;
}

export function SchemaBuilderProvider({
  children,
}: SchemaBuilderProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Table actions
  const addTable = useCallback(() => {
    const newTable: SchemaBuilderTable = {
      tempId: uuidv4(),
      name: "",
      displayName: "New Table",
      description: "",
      columns: [
        {
          tempId: uuidv4(),
          name: "id",
          displayName: "ID",
          dataType: "Text",
          isPrimaryKey: true,
          isRequired: true,
          sortOrder: 0,
        },
      ],
    };
    dispatch({ type: "ADD_TABLE", payload: newTable });
  }, []);

  const updateTable = useCallback(
    (tableId: string, updates: Partial<SchemaBuilderTable>) => {
      dispatch({ type: "UPDATE_TABLE", payload: { tableId, updates } });
    },
    [],
  );

  const deleteTable = useCallback((tableId: string) => {
    dispatch({ type: "DELETE_TABLE", payload: tableId });
  }, []);

  const selectTable = useCallback((tableId: string | null) => {
    dispatch({ type: "SELECT_TABLE", payload: tableId });
  }, []);

  // Column actions
  const addColumn = useCallback(
    (tableId: string) => {
      const table = state.tables.find((t) => t.tempId === tableId);
      const newColumn: SchemaBuilderColumn = {
        tempId: uuidv4(),
        name: "",
        displayName: "",
        dataType: "Text" as SimpleColumnType,
        isPrimaryKey: false,
        isRequired: false,
        sortOrder: table ? table.columns.length : 0,
      };
      dispatch({ type: "ADD_COLUMN", payload: { tableId, column: newColumn } });
    },
    [state.tables],
  );

  const updateColumn = useCallback(
    (
      tableId: string,
      columnId: string,
      updates: Partial<SchemaBuilderColumn>,
    ) => {
      dispatch({
        type: "UPDATE_COLUMN",
        payload: { tableId, columnId, updates },
      });
    },
    [],
  );

  const deleteColumn = useCallback((tableId: string, columnId: string) => {
    dispatch({ type: "DELETE_COLUMN", payload: { tableId, columnId } });
  }, []);

  const reorderColumns = useCallback(
    (tableId: string, columns: SchemaBuilderColumn[]) => {
      dispatch({ type: "REORDER_COLUMNS", payload: { tableId, columns } });
    },
    [],
  );

  // Relationship actions
  const addRelationship = useCallback(
    (
      sourceTableId: string,
      sourceColumnId: string,
      targetTableId: string,
      targetColumnId: string,
      onDelete: OnDeleteBehavior = "RESTRICT",
    ) => {
      const sourceTable = state.tables.find((t) => t.tempId === sourceTableId);
      const targetTable = state.tables.find((t) => t.tempId === targetTableId);
      const newRelationship: SchemaBuilderRelationship = {
        tempId: uuidv4(),
        sourceTableId,
        sourceColumnId,
        targetTableId,
        targetColumnId,
        displayName: `${sourceTable?.displayName || "Table"} -> ${targetTable?.displayName || "Table"}`,
        onDelete,
      };
      dispatch({ type: "ADD_RELATIONSHIP", payload: newRelationship });
    },
    [state.tables],
  );

  const deleteRelationship = useCallback((relationshipId: string) => {
    dispatch({ type: "DELETE_RELATIONSHIP", payload: relationshipId });
  }, []);

  // State actions
  const loadTables = useCallback((tables: SchemaBuilderTable[]) => {
    dispatch({ type: "LOAD_TABLES", payload: tables });
  }, []);

  const loadRelationships = useCallback(
    (relationships: SchemaBuilderRelationship[]) => {
      dispatch({ type: "LOAD_RELATIONSHIPS", payload: relationships });
    },
    [],
  );

  const setDirty = useCallback((isDirty: boolean) => {
    dispatch({ type: "SET_DIRTY", payload: isDirty });
  }, []);

  const setSaving = useCallback((isSaving: boolean) => {
    dispatch({ type: "SET_SAVING", payload: isSaving });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Helpers
  const getSelectedTable = useCallback(() => {
    return state.tables.find((t) => t.tempId === state.selectedTableId);
  }, [state.tables, state.selectedTableId]);

  const getTableById = useCallback(
    (tableId: string) => {
      return state.tables.find((t) => t.tempId === tableId);
    },
    [state.tables],
  );

  const value: SchemaBuilderContextValue = {
    state,
    addTable,
    updateTable,
    deleteTable,
    selectTable,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    addRelationship,
    deleteRelationship,
    loadTables,
    loadRelationships,
    setDirty,
    setSaving,
    setError,
    reset,
    getSelectedTable,
    getTableById,
  };

  return (
    <SchemaBuilderContext.Provider value={value}>
      {children}
    </SchemaBuilderContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useSchemaBuilderContext() {
  const context = useContext(SchemaBuilderContext);
  if (!context) {
    throw new Error(
      "useSchemaBuilderContext must be used within a SchemaBuilderProvider",
    );
  }
  return context;
}

export default SchemaBuilderContext;
